import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get, post, patch } from "../../lib/request"

function trimStr(v) {
  return String(v ?? "").trim()
}

function normalizeClientDetailRow(row) {
  if (!row || typeof row !== "object") return null
  const id = row.id
  if (id == null && !trimStr(row.name) && !trimStr(row.address)) {
    return null
  }

  return {
    id: id != null && id !== "" ? id : null,
    clientId: row.clientId ?? row.client_id ?? null,
    name: trimStr(row.name),
    rating: row.rating != null && row.rating !== "" ? Number(row.rating) : undefined,
    reviews: row.reviews != null && row.reviews !== "" && !Number.isNaN(Number(row.reviews))
      ? Number(row.reviews)
      : undefined,
    address: trimStr(row.address),
    contact: trimStr(row.contact),
    website: trimStr(row.website),
    email: trimStr(row.email),
    timezone: trimStr(
      row.timezone ||
        (typeof row.timeZone === "string" ? row.timeZone : row.timeZone?.name),
    ),
    timeZone: row.timeZone ?? null,
    roomRates: Array.isArray(row.roomRates) ? row.roomRates : [],
    checkin: trimStr(row.checkin ?? row.checkIn),
    checkout: trimStr(row.checkout ?? row.checkOut),
  }
}

function extractClientDetailList(payload) {
  if (payload == null) return []
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload.data)) return payload.data
  return []
}

/** GET /api/v1/client-details?clientIds={id} */
export const fetchClientDetailsByClientId = createAsyncThunk(
  "clientDetails/fetchByClientId",
  async (clientId, { rejectWithValue }) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }
      const res = await get(`client-details?clientIds=${encodeURIComponent(clientId)}`)
      const list = extractClientDetailList(res)
      const row = list[0] ?? null
      return {
        clientId,
        detail: normalizeClientDetailRow(row),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch client details")
    }
  },
)

/** POST /api/v1/client-details/clients/{clientId} */
export const createClientDetails = createAsyncThunk(
  "clientDetails/create",
  async ({ clientId, body }, { rejectWithValue }) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }
      const res = await post(`client-details/clients/${clientId}`, body)
      const saved = res?.data !== undefined ? res.data : res
      return normalizeClientDetailRow(saved) ?? { ...body, id: saved?.id ?? null }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to create client details")
    }
  },
)

/** PATCH /api/v1/client-details/{id} */
export const patchClientDetails = createAsyncThunk(
  "clientDetails/patch",
  async ({ id, body }, { rejectWithValue }) => {
    try {
      if (id == null || id === "") {
        return rejectWithValue("Client details id is required")
      }
      const res = await patch(`client-details/${id}`, body)
      const saved = res?.data !== undefined ? res.data : res
      return normalizeClientDetailRow(saved) ?? { ...body, id }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update client details")
    }
  },
)

export const saveClientDetails = createAsyncThunk(
  "clientDetails/save",
  async ({ clientId, body, detailId }, { getState, rejectWithValue, dispatch }) => {
    const existingId =
      detailId != null && detailId !== ""
        ? detailId
        : getState()?.clientDetails?.detail?.id

    try {
      if (existingId != null && existingId !== "") {
        return await dispatch(patchClientDetails({ id: existingId, body })).unwrap()
      }
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }
      return await dispatch(createClientDetails({ clientId, body })).unwrap()
    } catch (err) {
      return rejectWithValue(
        typeof err === "string" ? err : err?.message || "Failed to save client details",
      )
    }
  },
)

const clientDetailsSlice = createSlice({
  name: "clientDetails",
  initialState: {
    detail: null,
    loading: false,
    error: null,
    saving: false,
    saveError: null,
    lastFetchedClientId: null,
  },
  reducers: {
    clearClientDetails: (state) => {
      state.detail = null
      state.loading = false
      state.error = null
      state.saving = false
      state.saveError = null
      state.lastFetchedClientId = null
    },
    clearClientDetailsSaveError: (state) => {
      state.saveError = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientDetailsByClientId.pending, (state, action) => {
        state.loading = true
        state.error = null
        if (String(state.lastFetchedClientId ?? "") !== String(action.meta.arg ?? "")) {
          state.detail = null
        }
      })
      .addCase(fetchClientDetailsByClientId.fulfilled, (state, action) => {
        state.loading = false
        state.lastFetchedClientId = action.payload.clientId ?? null
        state.detail = action.payload.detail
      })
      .addCase(fetchClientDetailsByClientId.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? "Failed to load client details"
        state.detail = null
      })
      .addCase(createClientDetails.pending, (state) => {
        state.saving = true
        state.saveError = null
      })
      .addCase(createClientDetails.fulfilled, (state, action) => {
        state.saving = false
        state.detail = action.payload ?? state.detail
      })
      .addCase(createClientDetails.rejected, (state, action) => {
        state.saving = false
        state.saveError = action.payload ?? "Failed to create client details"
      })
      .addCase(patchClientDetails.pending, (state) => {
        state.saving = true
        state.saveError = null
      })
      .addCase(patchClientDetails.fulfilled, (state, action) => {
        state.saving = false
        state.detail = action.payload ?? state.detail
      })
      .addCase(patchClientDetails.rejected, (state, action) => {
        state.saving = false
        state.saveError = action.payload ?? "Failed to update client details"
      })
      .addCase(saveClientDetails.pending, (state) => {
        state.saving = true
        state.saveError = null
      })
      .addCase(saveClientDetails.fulfilled, (state, action) => {
        state.saving = false
        if (action.payload) state.detail = action.payload
      })
      .addCase(saveClientDetails.rejected, (state, action) => {
        state.saving = false
        state.saveError = action.payload ?? "Failed to save client details"
      })
  },
})

export const { clearClientDetails, clearClientDetailsSaveError } = clientDetailsSlice.actions
export default clientDetailsSlice.reducer
